import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, MessageCircle, Instagram, Facebook, Linkedin } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useSettings } from "@/context/SettingsContext";
import { waLink } from "@/lib/wa";

const FOOTER_NAV = [
  { to: "/our-coffee", key: "nav.coffee" },
  { to: "/available-stock", key: "nav.stock" },
  { to: "/our-warehouse", key: "nav.warehouse" },
  { to: "/export-journey", key: "nav.journey" },
  { to: "/quality", key: "nav.quality" },
  { to: "/about", key: "nav.about" },
  { to: "/contact", key: "nav.contact" },
];

export const Footer = () => {
  const { t } = useLang();
  const { settings } = useSettings();
  return (
    <footer data-testid="site-footer" className="grain bg-forest-dark text-offwhite">
      <div className="mx-auto max-w-[1400px] px-4 py-16 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-4">
          <div className="md:col-span-1">
            <img src={settings.logo || "/logo.jpg"} alt="Warehouse Coffee" className="mb-4 h-14 w-14 rounded bg-white p-1" />
            <div className="font-display text-lg font-extrabold tracking-tight">WAREHOUSE COFFEE</div>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-offwhite/50">Indonesian Arabica Green Coffee</p>
            <p className="mt-4 max-w-xs font-body text-sm leading-relaxed text-offwhite/65">
              {settings.tagline}
            </p>
            <div className="mt-5 flex gap-3">
              {settings.instagram && <a href={settings.instagram} target="_blank" rel="noreferrer" className="rounded-full border border-offwhite/20 p-2 hover:bg-offwhite/10"><Instagram className="h-4 w-4" /></a>}
              {settings.facebook && <a href={settings.facebook} target="_blank" rel="noreferrer" className="rounded-full border border-offwhite/20 p-2 hover:bg-offwhite/10"><Facebook className="h-4 w-4" /></a>}
              {settings.linkedin && <a href={settings.linkedin} target="_blank" rel="noreferrer" className="rounded-full border border-offwhite/20 p-2 hover:bg-offwhite/10"><Linkedin className="h-4 w-4" /></a>}
            </div>
          </div>

          <div>
            <h4 className="mb-4 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-amber-soft">Navigate</h4>
            <ul className="space-y-2.5">
              {FOOTER_NAV.map((n) => (
                <li key={n.to}>
                  <Link to={n.to} className="font-body text-sm text-offwhite/70 transition-colors hover:text-amber-soft">{t(n.key)}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="mb-4 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-amber-soft">Contact</h4>
            <ul className="space-y-3 font-body text-sm text-offwhite/70">
              {settings.email && <li className="flex items-start gap-2"><Mail className="mt-0.5 h-4 w-4 shrink-0 text-amber-soft" /><span>{settings.email}</span></li>}
              {settings.phone && <li className="flex items-start gap-2"><Phone className="mt-0.5 h-4 w-4 shrink-0 text-amber-soft" /><span>{settings.phone}</span></li>}
              {settings.address && <li className="flex items-start gap-2"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-amber-soft" /><span>{settings.address}</span></li>}
            </ul>
          </div>

          <div>
            <h4 className="mb-4 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-amber-soft">Get Started</h4>
            <Link to="/request-quote" data-testid="footer-quote-cta" className="mb-3 block rounded-full bg-amber-brand px-5 py-3 text-center font-display text-xs font-bold uppercase tracking-wider text-espresso-dark transition-all hover:bg-amber-soft">
              {t("cta.quote")}
            </Link>
            <a href={waLink(settings.whatsapp)} target="_blank" rel="noreferrer" data-testid="footer-whatsapp-cta" className="flex items-center justify-center gap-2 rounded-full border border-offwhite/25 px-5 py-3 font-display text-xs font-bold uppercase tracking-wider text-offwhite transition-all hover:bg-offwhite/10">
              <MessageCircle className="h-4 w-4" /> {t("cta.whatsapp")}
            </a>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-offwhite/10 pt-6 sm:flex-row">
          <p className="font-body text-xs text-offwhite/50">© {new Date().getFullYear()} Warehouse Coffee. {t("footer.rights")}</p>
          <Link to="/admin" className="font-mono text-[10px] uppercase tracking-widest text-offwhite/30 hover:text-offwhite/60">Admin</Link>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
